import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getMyEntitlements } from "@shared/services/capabilityService";

// tab → 所属产品模块（整模块授权口径；门诊拆为患者档案 / 挂号 / 门诊病历三个模块）。
export const TAB_CAPABILITY: Readonly<Record<string, string[]>> = {
  diagnosisTreatment: ["clinical"],
  syndromeDifferentiation: ["tcm"],
  // 病历书写页签只由 record（病历书写模块）触发；
  // 门诊病历模块（outpatient_record）走患者管理/病历编辑器，不触达 AI 工作台页签
  medicalWriting: ["record"],
  basicHealthCare: ["quality"],
  tcmHealthCare: ["health"],
  publicHealthService: ["chronic"],
  // 门诊：患者档案（患者/病历查询/标注/标签）、挂号（挂号/队列）、门诊病历
  outpatientPatient: ["outpatient_patient"],
  outpatientRegistration: ["outpatient_registration"],
  outpatientRecord: ["outpatient_patient", "outpatient_record"],
  // work 模块（chat/work 模式切换门控）
  workspace: ["workspace"],
};

/** 旧模块键（不兼容）；子组键（含冒号）与细码（含点）由格式正则排除。 */
const LEGACY_MODULE_KEYS: ReadonlySet<string> = new Set([
  "outpatient",
  "record_assistant",
]);

/** 整模块键格式；后端 catalog 是唯一权威，新增模块无需改前端白名单。 */
const MODULE_KEY_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;

export function isModuleKey(code: string): boolean {
  return MODULE_KEY_PATTERN.test(code) && !LEGACY_MODULE_KEYS.has(code);
}

/** 中医学派独立模块码前缀（tcm_<slug>）；学派模块均以此前缀登记。 */
const TCM_SCHOOL_MODULE_PREFIX = "tcm_";

export const useCapabilityStore = defineStore("capability", () => {
  const codes = ref<Set<string>>(new Set());
  const loaded = ref(false);
  const loading = ref(false);

  /**
   * 身份代次：登录、退出、账号/机构/科室变化时 +1。
   * 在途请求返回时若代次不再匹配，说明身份已切换，结果必须整体丢弃，
   * 防止旧账号的迟到响应把新身份的权益状态改回去。
   */
  let identityEpoch = 0;
  /** 当前代次的在途加载：同代次并发 load 复用同一请求（去重） */
  let inflight: { epoch: number; promise: Promise<void> } | null = null;

  const capabilityCodes = computed(() => [...codes.value].sort());
  /** 权益判定默认拒绝：未加载（含加载失败、已作废）时一律不授予。 */
  const has = (code: string): boolean => codes.value.has(code);
  const hasAny = (required: readonly string[]): boolean =>
    required.some((code) => has(code));
  const canUseTab = (tab: string): boolean => {
    // 理法方药页签：持有 tcm 或任一中医学派独立模块（tcm_*）即可进入；
    // 具体学派选项由 tcmSchoolRegistry.authorizationCode 单独过滤。
    if (tab === "syndromeDifferentiation") {
      return (
        has("tcm") ||
        capabilityCodes.value.some((code) =>
          code.startsWith(TCM_SCHOOL_MODULE_PREFIX),
        )
      );
    }
    return hasAny(TAB_CAPABILITY[tab] ?? []);
  };
  /**
   * 按给定页签顺序取首个已授权页签；权益未加载或一个都没授权时返回 null。
   * 用于未授权页签的回退目标（顺序即产品页签定义的展示顺序）。
   */
  const firstEntitledTab = (tabKeys: readonly string[]): string | null => {
    if (!loaded.value) return null;
    return tabKeys.find((tab) => canUseTab(tab)) ?? null;
  };

  /**
   * 作废当前权益并使在途请求的迟到结果失效。
   * 退出登录、账号/机构/科室变化、跨标签会话清理时调用，随后应重新 load()。
   */
  const invalidate = (): void => {
    identityEpoch += 1;
    inflight = null;
    codes.value = new Set();
    loaded.value = false;
    loading.value = false;
  };

  /**
   * 加载当前身份的业务能力。
   * - 失败时清除权益、保持默认拒绝，异常继续向上抛（由调用方决定提示）；
   * - 同代次并发调用复用同一请求；身份切换后返回的迟到响应被整体丢弃。
   */
  const load = async (): Promise<void> => {
    if (inflight && inflight.epoch === identityEpoch) {
      return inflight.promise;
    }

    const epoch = identityEpoch;
    loading.value = true;

    const promise = (async () => {
      try {
        const response = await getMyEntitlements();
        if (epoch !== identityEpoch) return; // 迟到响应：身份已切换，整体丢弃
        codes.value = new Set(
          response.items
            .filter((item) => item.enabled)
            .flatMap((item) =>
              isModuleKey(item.capability_code) ? [item.capability_code] : [],
            ),
        );
        loaded.value = true;
      } catch (error) {
        if (epoch === identityEpoch) {
          // 刷新失败：清除可能过期的权益，保持默认拒绝，等待下次成功加载
          codes.value = new Set();
          loaded.value = false;
        }
        throw error;
      } finally {
        if (epoch === identityEpoch) {
          loading.value = false;
          inflight = null;
        }
      }
    })();

    inflight = { epoch, promise };
    return promise;
  };

  /** 兼容旧调用：作废权益（等价于 invalidate）。 */
  const clear = (): void => {
    invalidate();
  };

  return {
    capabilityCodes,
    loaded,
    loading,
    has,
    hasAny,
    canUseTab,
    firstEntitledTab,
    load,
    invalidate,
    clear,
  };
});
